import { IconChevron, Alert, Text, TextLinkButton } from 'braid-design-system';
import { DateTime } from 'luxon';
import queryString from 'query-string';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import Routes from 'src/config/routes';
import { ListingStatus } from 'src/generated/graphql';
import type { ClientListingForListingsTab } from 'src/types/graphql-derived';
import { createPostListingUrl } from 'src/utils';

export interface AdMessageProps {
  listing: ClientListingForListingsTab;
  accountPaid: boolean;
  errorMessage?: string;
}

const AdMessage = ({ listing, accountPaid, errorMessage }: AdMessageProps) => {
  const navigate = useNavigate();
  const onClick = accountPaid
    ? () =>
        // Redirect to CASBA
        window.location.assign(
          createPostListingUrl(listing.clientId, listing.id),
        )
    : () =>
        navigate(
          queryString.stringifyUrl({
            url: Routes.PACKAGES,
            query: { listingId: listing.id },
          }),
        );

  if (errorMessage) {
    return (
      <Alert
        tone="critical"
        data={{ testid: 'dashboard-ad-message-error-message' }}
      >
        <Text>{errorMessage}</Text>
      </Alert>
    );
  }

  if (listing.status === ListingStatus.Draft) {
    return (
      <AdAlert
        prompt="This ad is still in draft, "
        callToAction="post it now"
        onClick={onClick}
      />
    );
  }

  const warningDate = new Date();
  warningDate.setDate(warningDate.getDate() + 7);
  const isAboutToExpire =
    listing.status === ListingStatus.Active &&
    listing.expiryDate &&
    DateTime.fromISO(listing.expiryDate).toJSDate() < warningDate &&
    !listing.autoRenew &&
    listing.creditRemaining > 0;

  if (isAboutToExpire) {
    return (
      <AdAlert
        prompt="This ad is about to expire, "
        callToAction="repost it now"
        onClick={onClick}
      />
    );
  }

  return null;
};

export default AdMessage;

interface AdAlertProps {
  prompt: string;
  callToAction: string;
  onClick: () => void;
}

const AdAlert = ({ prompt, callToAction, onClick }: AdAlertProps) => (
  <Alert tone="caution" data={{ testid: 'listings-tab-table-row-alert' }}>
    <Text>
      {prompt}
      <TextLinkButton onClick={onClick}>
        {callToAction}
        <IconChevron direction="right" />
      </TextLinkButton>
    </Text>
  </Alert>
);
